import { AfterViewChecked, Component, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@shared/component/base/base.component';
import {
  Collection,
  Dictionary, PropertyCollection,
  PropertyService, ServicePaymentMethod, ServicePaymentMethodService, ServicePrice,
  ServicePriceCollection, ServicePriceService, ServiceProduct,
  ServiceProductCollection,
  ServiceProductService, ServiceSubscription,
  ServiceSubscriptionItem, SubscriptionMessagesEnum, SubscriptionService, ToastService
} from 'taxtank-core';
import { plainToClass } from 'class-transformer';
import { ALL_FEATURES, FEATURES_BY_PRODUCT } from './features.const';
import { combineLatest, tap } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import {
  PRODUCTS_TOOLTIPS
} from '@client/shared/components/subscription/subscription-change-dialog/products-tooltips.enum';
import {
  PaymentDialogComponent
} from '@client/my-account/my-account-subscription/components/payment-methods/payment-dialog/payment-dialog.component';
import { ConfirmationService } from 'primeng/api';
import clone from 'lodash/clone';
import {
  AddPaymentMethodDialogComponent
} from '@client/my-account/my-account-subscription/components/payment-methods/add-payment-method-dialog/add-payment-method-dialog.component';
import {
  SubscriptionChangeDisclaimerComponent
} from '@client/shared/components/subscription/subscription-change-disclaimer/subscription-change-disclaimer.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';

/**
 * @TODO vik refactor
 * @TODO Alex: Component is not finished. Finish during TT-2728
 */
@Component({
  selector: 'app-subscription-change-dialog',
  templateUrl: './subscription-change-dialog.component.html',
  styleUrls: ['./subscription-change-dialog.component.scss'],
  standalone: false
})
export class SubscriptionChangeDialogComponent extends BaseComponent implements OnInit, AfterViewChecked {
  products: ServiceProductCollection;
  prices: ServicePriceCollection;
  pricesByProduct: Dictionary<ServicePrice>;
  discountPricesByProduct: Dictionary<number>;
  propertyPrice: ServicePrice;
  propertyProduct: ServiceProduct;
  isPackage: boolean;
  subscription = plainToClass(ServiceSubscription, { items: [] });
  propertiesQty: number;
  propertiesMinQty: number;
  propertiesMaxQty: number;
  features: string[] = ALL_FEATURES;
  tooltips = PRODUCTS_TOOLTIPS;
  featuresByProduct = FEATURES_BY_PRODUCT;
  properties: PropertyCollection;
  firstPayment: boolean;
  paymentMethods: Collection<ServicePaymentMethod>;
  isHidden = false;
  form = new FormGroup({ checkbox: new FormControl(false, Validators.requiredTrue) });

  private serviceProductService = inject(ServiceProductService);
  private servicePriceService = inject(ServicePriceService);
  private subscriptionService = inject(SubscriptionService);
  private paymentMethodService = inject(ServicePaymentMethodService);
  private propertyService = inject(PropertyService);
  public dialogRef = inject(MatDialogRef<SubscriptionChangeDialogComponent>);
  private dialog = inject(MatDialog);
  private confirmationService = inject(ConfirmationService);
  protected toastService = inject(ToastService);

  ngOnInit(): void {
    this.paymentMethodService.get().subscribe(paymentMethods => {
      this.paymentMethods = paymentMethods;
    });

    this.subscriptionService.get().pipe(
      take(1),
      map(subscriptions => subscriptions.getActive().getPaid().first ?? this.subscription),
      tap(subscription => {
        if (subscription.isAnnual()) {
          return;
        }

        this.isHidden = true;

        this.dialog.open(SubscriptionChangeDisclaimerComponent, {
          panelClass: 'dialog-large',
          data: { dialogRef: this.dialogRef }
        }).afterClosed()
          .pipe(take(1))
          .subscribe(() => this.isHidden = false);
      })
    ).subscribe();

    combineLatest([
      this.serviceProductService.get(),
      this.servicePriceService.get(),
      this.subscriptionService.get(),
      this.propertyService.get()
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([products, prices, subscriptions, properties]) => {
        // User may have only 1 paid subscription
        this.firstPayment = !subscriptions.getPaid().length;
        // TODO vik
        this.isPackage = this.subscription.isFullPackage();
        this.prices = prices.getActive();
        this.subscription = subscriptions.getActivePaid()?.upgrade(this.prices) ?? this.subscription;
        this.pricesByProduct = new Dictionary(this.prices.annual.toArray(), 'product.id');
        this.products = products.getActive();

        // Properties quantity
        this.propertyProduct = this.products.findBy('propertyTank', true);
        this.properties = properties;
        this.propertiesMaxQty = this.propertyProduct.maxQty;
        this.propertiesMinQty = Math.max(this.propertyProduct.minQty, this.properties.length);
        // set current properties quantity if exist or default based on minQty and actual properties length
        if (this.subscription.propertyTankItem) {
          this.propertiesQty = this.subscription.propertyTankItem.quantity;
        } else {
          this.propertiesQty = Math.max(this.propertiesMinQty, this.properties.length);
        }

        this.setPrices();
      });
  }

  /**
   * @TODO Alex: hack: avoid ChangeBeforeCkecked error related to mat-checkbox
   * https://stackoverflow.com/questions/39787038/how-to-manage-angular2-expression-has-changed-after-it-was-checked-exception-w
   */
  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  /**
   * @TODO Alex: move to model/collection, bad performance
   */
  get featuresJoin(): string {
    return this.subscription.getItems().mapBy('price.product.id').map(
      (productId: number) => this.featuresByProduct[this.products.findBy('id', productId).title]
    ).join(',');
  }

  /**
   * Select/Deselect subscription product
   */
  toggleProduct(product: ServiceProduct): void {
    this.subscription.hasProduct(product) ? this.removeProduct(product) : this.addProduct(product);
    console.log(this.subscription);
  }

  /**
   * Add passed product to subscription
   */
  private addProduct(product: ServiceProduct): void {
    // @TODO Alex: improve logic
    this.subscription.items.push(plainToClass(ServiceSubscriptionItem, {
      price: this.pricesByProduct.get(product.id),
      quantity: product.isProperties() ? this.propertiesQty : 1
    }));
    this.setPrices();
  }

  /**
   * Remove passed product from subscription
   */
  private removeProduct(product: ServiceProduct): void {
    this.subscription.items = this.subscription.items.filter((item) => item.price.product.id !== product.id);
    this.setPrices();
  }

  /**
   * Update current subscription property item with selected quantity
   */
  updatePropertiesQuantity(): void {
    this.subscription.propertyTankItem.quantity = this.propertiesQty;
    this.setPrices();
  }

  setPrices(): void {
    this.discountPricesByProduct = this.subscription.getDiscountPriceByProduct(this.prices.annual.toArray());
    // @TODO TT-5638 remove after tests
    // this.propertyPrice = this.prices.getProperty();

    // this.subscription.items.forEach((item) => {
    //   this.removeProduct(item.price.product);
    //   this.addProduct(item.price.product);
    // });

    // if (this.subscription.promoCode) {
    //   this.subscription.applyPromoCode(this.subscription.promoCode);
    // }
  }

  upgrade(): void {
    // payment method required for temporary free subscription
    if (this.subscription.price || this.subscription.promoCode.isDurationForever() || this.paymentMethods.length) {
      this.postSubscription();
      return;
    }

    this.openAddPaymentDialog(() => this.postSubscription());
  }

  openAddPaymentDialog(onConfirm: () => void): void {
    const dialogRef = this.dialog.open(AddPaymentMethodDialogComponent, { panelClass: 'dialog-small' });

    dialogRef.afterClosed().subscribe((isConfirmed: boolean): void => {
      if (!isConfirmed) {
        return;
      }

      onConfirm();
    });
  }

  /**
   * check subscription and handle result (continue with payment or close if payment if not required)
   */
  postSubscription(): void {
    // Send prepared subscription to backend to get Stripe client secret
    this.subscriptionService.postStripeSubscription(this.subscription)
      .pipe(takeUntil(this.destroy$))
      .subscribe((clientSecret: string) => {
        // empty response for 100% discount via promocode (no need for payment)
        if (!clientSecret) {
          this.toastService.success(SubscriptionMessagesEnum.SUCCEEDED_FREE);
          this.dialogRef.close();

          return;
        }

        // continue with payment
        this.openPaymentDialog(clientSecret);
      });
  }

  /**
   * Show dialog with Stripe payment form
   */
  openPaymentDialog(clientSecret: string): void {
    this.dialog.open(PaymentDialogComponent, {
      data: {
        subscription: this.subscription,
        stripeClientSecret: clientSecret
      },
      panelClass: 'dialog-small'
    });

    this.dialogRef.close();
  }

  /**
   * Show confirmation popup with proration cost
   */
  openChangeSubscriptionConfirmPopup(event: Event): void {
    this.subscriptionService.getProrationCost(this.subscription).subscribe((prorationCost: number) => {
      const date = clone(this.subscription.startDate);
      date.setMonth(date.getMonth() + 12);
      const dateString = date.toLocaleDateString('en-AU');
      const isDowngrade = prorationCost < 0;


      // @TODO Alex no way we keep that
      let confirmPopupContent = `
        <div class=" fs-big fw-bold mb-10">
          Change Subscription Plan
        </div>
      `;

      if (isDowngrade) {
        confirmPopupContent += `
          <div class="c-secondary">
            Downgrade will take affect from the next period <b>${dateString}</b>,<br/>
            you will be charged <h5 class="d-inline">$${this.subscription.price.toFixed(2)}</h5> next time.
          </div>
        `;
      } else {
        confirmPopupContent += `
          <div class="c-secondary">
            Upgrade will take affect right away, you will be charged <h5
            class="d-inline">$${prorationCost.toFixed(2)}</h5> right now.<br/>
            Your annual payment will be
            <h5 class="d-inline">$${this.subscription.price.toFixed(2)}</h5> from <b>${dateString}</b>.
          </div>
        `;
      }

      this.confirmationService.confirm({
        target: event.target,
        acceptLabel: 'Confirm',
        rejectLabel: 'Cancel',
        message: confirmPopupContent,
        accept: () => {
          this.paymentMethods.length ? this.changeSubscription() : this.openAddPaymentDialog(() => this.changeSubscription());
        }
      });
    });
  }

  changeSubscription() : void {
    this.subscriptionService.changeSubscription(this.subscription).subscribe(() => {
      this.toastService.warning('Please wait while we process your payment.');
      this.dialogRef.close();
    });
  }

  onSubmit($event: Event): void {
    this.form.get('checkbox').markAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.subscription.id ? this.openChangeSubscriptionConfirmPopup($event) : this.upgrade();
  }
}
